// Catálogo de productos y servicios. Inventario → Catálogo → Productos.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ImageIcon, Loader2, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Categoria, DIAS_SEMANA_MIN, Marca, LETRAS_DESCUENTO, NATURALEZA, Producto, ProductoDescuento, Switch, TIPO_PRODUCTO, deleteProductoImagen, fieldCls, getProductoImagen, inv, invRpc, resizeToDataURL, upsertProductoImagen } from './shared';
import { useSort, SortTh } from '@/features/control-bancario/shared';
import { LOCALE } from '@/lib/utils/regional';
import { useTranslation } from 'react-i18next';

const usd = (v: number | string | null) => (v == null ? '—' : Number(v).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' }));

const COMPORTAMIENTOS: { v: keyof Producto; label: string }[] = [
  { v: 'es_vendible', label: 'productos.c_vendible' },
  { v: 'es_comprable', label: 'productos.c_comprable' },
  { v: 'es_facturable', label: 'productos.c_facturable' },
  { v: 'controla_stock', label: 'productos.c_controla_stock' },
  { v: 'maneja_lote', label: 'productos.c_maneja_lote' },
  { v: 'maneja_caducidad', label: 'productos.c_maneja_caducidad' },
  { v: 'requiere_receta', label: 'productos.c_requiere_receta' },
];

const vacio = {
  categoria_id: '', codigo: '', nombre: '', descripcion: '', tipo_producto: 'fisico', naturaleza: '',
  es_vendible: true, es_comprable: true, es_facturable: true,
  controla_stock: false, maneja_lote: false, maneja_caducidad: false, requiere_receta: false, apto_tv: false, es_regulado: false, es_esencial: false, es_estacional: false, es_nuevo: false,
  unidad_medida: 'U', presentacion: '', fracciones: 1, codigo_barras1: '', codigo_barras2: '', codigo_barras3: '',
  precio_base: 0, costo_referencial: 0, moneda: 'USD', iva_tarifa: 15,
  principio_activo: '', concentracion: '', marca_id: '', codigo_tarifario: '', codigo_cum: '', es_activo: true,
  participa_debito: true, participa_credito: true, solo_efectivo: false,
};

// Deriva las fracciones desde el texto de la presentación: el número que sigue a la
// "x" (ej. "Caja x 14" → 14). Si ese número va seguido de una unidad de peso/volumen/
// dosis ("Frasco x 120 ml", "Aerosol x 200 dosis") es una medida, no fracciones → 1.
export function fraccionesDePresentacion(pres: string): number {
  const p = (pres || '').trim();
  if (!p) return 1;
  if (/x\s*\d+\s*(ml|mg|kg|g|cc|gr|oz|dosis|litros?|l)\b/i.test(p)) return 1;
  const m = p.match(/x\s*(\d+)/i);
  return m ? Math.max(1, parseInt(m[1], 10)) : 1;
}

export function Productos() {
  const { t } = useTranslation('inventario');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [q, setQ] = useState('');
  const [fcat, setFcat] = useState('');
  const [ftipo, setFtipo] = useState('');
  const [fiva, setFiva] = useState('');
  const [festado, setFestado] = useState('');   // '' | 'activo' | 'inactivo'
  const [fcomps, setFcomps] = useState<string[]>([]);   // claves de comportamiento (varias)
  const toggleComp = (v: string) => setFcomps((arr) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]));
  const { data: prods = [], isFetching } = useQuery<Producto[]>({
    queryKey: ['inv-productos'], queryFn: () => inv.list<Producto>('productos', '?select=*&parent_id=is.null&order=nombre'),
  });
  const { data: cats = [] } = useQuery<Categoria[]>({
    queryKey: ['inv-categorias'], queryFn: () => inv.list<Categoria>('categorias', '?select=id,nombre,parent_id,codigo,descripcion,orden,es_activo'),
  });
  const { data: marcas = [] } = useQuery<Marca[]>({
    queryKey: ['inv-marcas'], queryFn: () => inv.list<Marca>('marca', '?select=id,nombre,codigo,descripcion,es_activo&order=nombre'),
  });
  const catName = (id: string | null) => cats.find((c) => c.id === id)?.nombre ?? '—';

  const ivas = useMemo(
    () => Array.from(new Set(prods.map((p) => Number(p.iva_tarifa)))).sort((a, b) => a - b),
    [prods],
  );

  const filtrados = useMemo(() => {
    const s = q.trim().toLowerCase();
    return prods.filter((p) => {
      if (s && !(p.nombre.toLowerCase().includes(s) || p.codigo.toLowerCase().includes(s))) return false;
      if (fcat && p.categoria_id !== fcat) return false;
      if (ftipo && p.tipo_producto !== ftipo) return false;
      if (fiva !== '' && Number(p.iva_tarifa) !== Number(fiva)) return false;
      if (festado === 'activo' && !p.es_activo) return false;
      if (festado === 'inactivo' && p.es_activo) return false;
      if (fcomps.length && !fcomps.every((k) => p[k as keyof Producto])) return false;
      return true;
    });
  }, [prods, q, fcat, ftipo, fiva, festado, fcomps]);

  const hayFiltros = !!(q || fcat || ftipo || fiva !== '' || festado || fcomps.length);
  const limpiar = () => { setQ(''); setFcat(''); setFtipo(''); setFiva(''); setFestado(''); setFcomps([]); };

  // Orden por columna (asc/desc) del catálogo
  const sort = useSort<Producto>(filtrados, (p, k) => {
    switch (k) {
      case 'codigo': return p.codigo;
      case 'nombre': return p.nombre;
      case 'tipo': return p.tipo_producto;
      case 'categoria': return catName(p.categoria_id);
      case 'precio': return Number(p.precio_base);
      case 'iva': return Number(p.iva_tarifa);
      case 'estado': return p.es_activo ? 1 : 0;
      default: return null;
    }
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const [tab, setTab] = useState<'general' | 'variantes' | 'descuentos' | 'imagen'>('general');
  const set = (patch: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...patch }));
  // Precio con IVA (espejo del precio base): buffer propio para no "saltar" al teclear.
  const [pcIva, setPcIva] = useState<string>('');
  const conIvaStr = (base: number, iva: number) => {
    const v = (Number(base) || 0) * (1 + (Number(iva) || 0) / 100);
    return v ? String(Number(v.toFixed(4))) : '0';
  };

  const abrir = (p?: Producto) => {
    if (p) {
      setEditId(p.id);
      setForm({
        categoria_id: p.categoria_id ?? '', codigo: p.codigo, nombre: p.nombre, descripcion: p.descripcion ?? '',
        tipo_producto: p.tipo_producto, naturaleza: p.naturaleza ?? '',
        es_vendible: p.es_vendible, es_comprable: p.es_comprable, es_facturable: p.es_facturable,
        controla_stock: p.controla_stock, maneja_lote: p.maneja_lote, maneja_caducidad: p.maneja_caducidad, requiere_receta: p.requiere_receta, apto_tv: p.apto_tv ?? false, es_regulado: p.es_regulado ?? false, es_esencial: p.es_esencial ?? false, es_estacional: p.es_estacional ?? false, es_nuevo: p.es_nuevo ?? false,
        unidad_medida: p.unidad_medida, presentacion: p.presentacion ?? '', fracciones: p.fracciones ?? 1,
        codigo_barras1: p.codigo_barras1 ?? '', codigo_barras2: p.codigo_barras2 ?? '', codigo_barras3: p.codigo_barras3 ?? '',
        precio_base: p.precio_base, costo_referencial: p.costo_referencial,
        moneda: p.moneda, iva_tarifa: p.iva_tarifa, principio_activo: p.principio_activo ?? '', concentracion: p.concentracion ?? '',
        marca_id: p.marca_id ?? '', codigo_tarifario: p.codigo_tarifario ?? '', codigo_cum: p.codigo_cum ?? '', es_activo: p.es_activo,
        participa_debito: p.participa_debito ?? true, participa_credito: p.participa_credito ?? true, solo_efectivo: p.solo_efectivo ?? false,
      });
      setPcIva(conIvaStr(p.precio_base, p.iva_tarifa));
    } else { setEditId(null); setForm(vacio); setPcIva(conIvaStr(vacio.precio_base, vacio.iva_tarifa)); }
    setTab('general');
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const body = {
        ...form, categoria_id: form.categoria_id || null, naturaleza: form.naturaleza || null,
        presentacion: form.presentacion || null, fracciones: Number(form.fracciones) || 1, descripcion: form.descripcion || null,
        codigo_barras1: form.codigo_barras1 || null, codigo_barras2: form.codigo_barras2 || null, codigo_barras3: form.codigo_barras3 || null,
        principio_activo: form.principio_activo || null, concentracion: form.concentracion || null,
        marca_id: form.marca_id || null, codigo_tarifario: form.codigo_tarifario || null, codigo_cum: form.codigo_cum || null,
        precio_base: Number(form.precio_base) || 0, costo_referencial: Number(form.costo_referencial) || 0, iva_tarifa: Number(form.iva_tarifa) || 0,
      };
      return editId ? inv.update('productos', editId, body) : inv.create('productos', body);
    },
    onSuccess: () => { toast.success(t('productos.guardado')); setAbierto(false); void qc.invalidateQueries({ queryKey: ['inv-productos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => inv.remove('productos', id),
    onSuccess: () => { toast.success(t('productos.eliminado')); void qc.invalidateQueries({ queryKey: ['inv-productos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      {abierto ? (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setAbierto(false)}><ArrowLeft className="w-4 h-4 mr-1" /> {t('comun.volver')}</Button>
          <h1 className="text-xl font-semibold">{editId ? t('productos.editar') : t('productos.nuevo_titulo')}</h1>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-semibold">{t('productos.titulo')}</h1>
          <div className="flex items-center gap-2">
            <input className={`${fieldCls} w-64`} placeholder={t('productos.buscar_ph')} value={q} onChange={(e) => setQ(e.target.value)} />
            <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> {t('productos.nuevo')}</Button>
          </div>
        </div>
      )}
      {!abierto && (<>
      <Card>
        <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-4">
          <Campo label={t('productos.categoria')}>
            <select className={fieldCls} value={fcat} onChange={(e) => setFcat(e.target.value)}>
              <option value="">{t('productos.todas')}</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </Campo>
          <Campo label={t('productos.tipo')}>
            <select className={fieldCls} value={ftipo} onChange={(e) => setFtipo(e.target.value)}>
              <option value="">{t('productos.todos')}</option>
              {TIPO_PRODUCTO.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
            </select>
          </Campo>
          <Campo label="IVA %">
            <select className={fieldCls} value={fiva} onChange={(e) => setFiva(e.target.value)}>
              <option value="">{t('productos.todos')}</option>
              {ivas.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Campo>
          <Campo label={t('productos.estado')}>
            <select className={fieldCls} value={festado} onChange={(e) => setFestado(e.target.value)}>
              <option value="">{t('productos.todos')}</option>
              <option value="activo">{t('comun.activo')}</option>
              <option value="inactivo">{t('comun.inactivo')}</option>
            </select>
          </Campo>
          <Campo label={t('productos.comportamiento')}>
            <details className="relative">
              <summary className={`${fieldCls} flex items-center cursor-pointer list-none [&::-webkit-details-marker]:hidden`}>
                {fcomps.length ? t('productos.n_seleccionados', { n: fcomps.length }) : t('productos.cualquiera')}
              </summary>
              <div className="absolute z-20 mt-1 w-56 space-y-1 rounded-md border bg-card p-2 shadow-lg">
                {COMPORTAMIENTOS.map((c) => (
                  <label key={c.v} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input type="checkbox" className="h-4 w-4 rounded border-input" checked={fcomps.includes(c.v)} onChange={() => toggleComp(c.v)} />
                    {t(c.label)}
                  </label>
                ))}
              </div>
            </details>
          </Campo>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">{t('productos.catalogo', { n: filtrados.length })}</CardTitle>
          {hayFiltros && <button className="text-xs text-primary hover:underline" onClick={limpiar}>{t('productos.limpiar_filtros')}</button>}
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 w-10 text-right">{t('comun.n_col')}</th>
              <SortTh col="codigo" sort={sort}>{t('productos.codigo')}</SortTh>
              <SortTh col="nombre" sort={sort}>{t('productos.nombre')}</SortTh>
              <SortTh col="tipo" sort={sort}>{t('productos.tipo')}</SortTh>
              <SortTh col="categoria" sort={sort}>{t('productos.categoria')}</SortTh>
              <SortTh col="precio" sort={sort} align="right">{t('productos.p_base')}</SortTh>
              <SortTh col="iva" sort={sort} align="right">IVA%</SortTh>
              <SortTh col="estado" sort={sort}>{t('productos.estado')}</SortTh>
              <th className="py-2"></th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">{t('productos.sin_productos')}</td></tr>}
              {sort.sorted.map((p, i) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-muted-foreground tabular-nums">{i + 1}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{p.codigo}</td>
                  <td className="py-2 pr-3">{p.nombre}{p.tiene_variantes && <span className="ml-2 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs">{t('productos.variantes_badge')}</span>}</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{p.tipo_producto}</span></td>
                  <td className="py-2 pr-3 text-muted-foreground">{catName(p.categoria_id)}</td>
                  <td className="py-2 pr-3 text-right">{usd(p.precio_base)}</td>
                  <td className="py-2 pr-3 text-right">{Number(p.iva_tarifa)}</td>
                  <td className="py-2 pr-3">{p.es_activo ? t('comun.activo') : t('comun.inactivo')}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title={t('comun.editar')} onClick={() => abrir(p)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title={t('productos.eliminar')}
                      onClick={async () => { if (await dlg.confirmar(t('productos.confirmar_eliminar', { nombre: p.nombre }), { peligro: true })) eliminar.mutate(p.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      </>)}

      {abierto && (
        <Card>
          <CardContent className="pt-4">
          <div className="space-y-4">
            <div className="flex gap-1 border-b">
              <button type="button" onClick={() => setTab('general')} className={`px-3 py-1.5 text-sm border-b-2 ${tab === 'general' ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground'}`}>{t('productos.tab_general')}</button>
              <button type="button" onClick={() => setTab('variantes')} className={`px-3 py-1.5 text-sm border-b-2 ${tab === 'variantes' ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground'}`}>{t('productos.tab_variantes')}</button>
              <button type="button" onClick={() => setTab('descuentos')} className={`px-3 py-1.5 text-sm border-b-2 ${tab === 'descuentos' ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground'}`}>{t('productos.tab_descuentos')}</button>
              <button type="button" onClick={() => setTab('imagen')} className={`px-3 py-1.5 text-sm border-b-2 ${tab === 'imagen' ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground'}`}>{t('productos.tab_imagen')}</button>
            </div>

            {tab === 'general' && (<>
            <section className="space-y-3">
              <p className="text-xs font-semibold uppercase text-muted-foreground">{t('productos.identidad')}</p>
              <div className="grid grid-cols-2 gap-3">
                <Campo label={editId ? t('productos.codigo_req') : t('productos.codigo')}><input className={fieldCls} value={form.codigo} onChange={(e) => set({ codigo: e.target.value })} placeholder={editId ? '' : t('productos.codigo_auto')} /></Campo>
                <Campo label={t('productos.categoria')}>
                  <select className={fieldCls} value={form.categoria_id} onChange={(e) => set({ categoria_id: e.target.value })}>
                    <option value="">{t('productos.sin_categoria')}</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </Campo>
              </div>
              <Campo label={t('productos.nombre_req')}><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
              <Campo label={t('productos.descripcion')}><textarea className={`${fieldCls} h-16`} value={form.descripcion} onChange={(e) => set({ descripcion: e.target.value })} /></Campo>
              <div className="grid grid-cols-2 gap-3">
                <Campo label={t('productos.tipo')}>
                  <select className={fieldCls} value={form.tipo_producto} onChange={(e) => set({ tipo_producto: e.target.value })}>
                    {TIPO_PRODUCTO.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
                  </select>
                </Campo>
                <Campo label={t('productos.naturaleza')}>
                  <select className={fieldCls} value={form.naturaleza} onChange={(e) => set({ naturaleza: e.target.value })}>
                    <option value="">—</option>
                    {NATURALEZA.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </Campo>
              </div>
            </section>

            <section className="space-y-2">
              <p className="text-xs font-semibold uppercase text-muted-foreground">{t('productos.comportamiento')}</p>
              <div className="grid grid-cols-2 gap-2">
                <Switch checked={form.es_vendible} onChange={(v) => set({ es_vendible: v })} label={t('productos.c_vendible')} />
                <Switch checked={form.es_comprable} onChange={(v) => set({ es_comprable: v })} label={t('productos.c_comprable')} />
                <Switch checked={form.es_facturable} onChange={(v) => set({ es_facturable: v })} label={t('productos.c_facturable')} />
                <Switch checked={form.controla_stock} onChange={(v) => set({ controla_stock: v })} label={t('productos.c_controla_stock')} />
                <Switch checked={form.maneja_lote} onChange={(v) => set({ maneja_lote: v })} label={t('productos.c_maneja_lote')} />
                <Switch checked={form.maneja_caducidad} onChange={(v) => set({ maneja_caducidad: v })} label={t('productos.c_maneja_caducidad')} />
                <Switch checked={form.requiere_receta} onChange={(v) => set({ requiere_receta: v })} label={t('productos.c_requiere_receta')} />
                <Switch checked={form.es_regulado} onChange={(v) => set({ es_regulado: v })} label={t('productos.c_regulado')} />
                <Switch checked={form.es_esencial} onChange={(v) => set({ es_esencial: v })} label={t('productos.c_esencial')} />
                <Switch checked={form.es_estacional} onChange={(v) => set({ es_estacional: v })} label={t('productos.c_estacional')} />
                <Switch checked={form.es_nuevo} onChange={(v) => set({ es_nuevo: v })} label={t('productos.c_nuevo')} />
                <Switch checked={form.apto_tv} onChange={(v) => set({ apto_tv: v })} label={t('productos.c_apto_tv')} />
              </div>
            </section>

            <section className="space-y-2">
              <p className="text-xs font-semibold uppercase text-muted-foreground">{t('productos.formas_cobro')}</p>
              <div className="grid grid-cols-2 gap-2">
                <Switch checked={form.solo_efectivo} onChange={(v) => set({ solo_efectivo: v, ...(v ? { participa_debito: false, participa_credito: false } : {}) })} label={t('productos.solo_efectivo')} />
                <span />
                <div className={form.solo_efectivo ? 'pointer-events-none opacity-50' : ''}>
                  <Switch checked={form.participa_debito} onChange={(v) => set({ participa_debito: v })} label={t('productos.aplica_debito')} />
                </div>
                <div className={form.solo_efectivo ? 'pointer-events-none opacity-50' : ''}>
                  <Switch checked={form.participa_credito} onChange={(v) => set({ participa_credito: v })} label={t('productos.aplica_credito')} />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {t('productos.solo_efectivo_ayuda')}
              </p>
            </section>

            <section className="space-y-3">
              <p className="text-xs font-semibold uppercase text-muted-foreground">{t('productos.comercial')}</p>
              <div className="grid grid-cols-3 gap-3">
                <Campo label={t('productos.unidad')}><input className={fieldCls} value={form.unidad_medida} onChange={(e) => set({ unidad_medida: e.target.value })} /></Campo>
                <Campo label={t('productos.presentacion')}><input className={fieldCls} value={form.presentacion} placeholder={t('productos.presentacion_ph')}
                  onChange={(e) => set({ presentacion: e.target.value, fracciones: fraccionesDePresentacion(e.target.value) })} /></Campo>
                <Campo label={t('productos.fracciones')}>
                  <input type="number" min="1" step="1" className={fieldCls} value={form.fracciones} onChange={(e) => set({ fracciones: Number(e.target.value) })} />
                  <span className="mt-1 block text-[11px] text-muted-foreground">{t('productos.fracciones_ayuda')}</span>
                </Campo>
                <Campo label="IVA %"><input type="number" step="0.01" className={fieldCls} value={form.iva_tarifa}
                  onChange={(e) => { const iva = Number(e.target.value); set({ iva_tarifa: iva }); setPcIva(conIvaStr(form.precio_base, iva)); }} /></Campo>
                <Campo label={t('productos.precio_base')}><input type="number" step="0.0001" className={fieldCls} value={form.precio_base}
                  onChange={(e) => { const base = Number(e.target.value); set({ precio_base: base }); setPcIva(conIvaStr(base, form.iva_tarifa)); }} /></Campo>
                <Campo label={t('productos.precio_iva')}><input type="number" step="0.01" className={fieldCls} value={pcIva}
                  onChange={(e) => { setPcIva(e.target.value); const v = Number(e.target.value); set({ precio_base: v > 0 ? v / (1 + (Number(form.iva_tarifa) || 0) / 100) : 0 }); }} /></Campo>
                <Campo label={t('productos.costo_ref')}><input type="number" step="0.0001" className={fieldCls} value={form.costo_referencial} onChange={(e) => set({ costo_referencial: Number(e.target.value) })} /></Campo>
                <Campo label={t('productos.moneda')}><input className={fieldCls} value={form.moneda} onChange={(e) => set({ moneda: e.target.value })} /></Campo>
              </div>
            </section>

            <section className="space-y-3">
              <p className="text-xs font-semibold uppercase text-muted-foreground">{t('productos.codigos_barras')}</p>
              <div className="grid grid-cols-3 gap-3">
                <Campo label={t('productos.barras', { n: 1 })}><input className={fieldCls} value={form.codigo_barras1} onChange={(e) => set({ codigo_barras1: e.target.value })} /></Campo>
                <Campo label={t('productos.barras', { n: 2 })}><input className={fieldCls} value={form.codigo_barras2} onChange={(e) => set({ codigo_barras2: e.target.value })} /></Campo>
                <Campo label={t('productos.barras', { n: 3 })}><input className={fieldCls} value={form.codigo_barras3} onChange={(e) => set({ codigo_barras3: e.target.value })} /></Campo>
              </div>
            </section>

            <section className="space-y-3">
              <p className="text-xs font-semibold uppercase text-muted-foreground">{t('productos.medico')}</p>
              <div className="grid grid-cols-3 gap-3">
                <Campo label={t('productos.principio_activo')}><input className={fieldCls} value={form.principio_activo} onChange={(e) => set({ principio_activo: e.target.value })} /></Campo>
                <Campo label={t('productos.concentracion')}><input className={fieldCls} value={form.concentracion} onChange={(e) => set({ concentracion: e.target.value })} /></Campo>
                <Campo label={t('productos.marca')}>
                  <select className={fieldCls} value={form.marca_id} onChange={(e) => set({ marca_id: e.target.value })}>
                    <option value="">{t('productos.sin_marca')}</option>
                    {marcas.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                  </select>
                </Campo>
                <Campo label={t('productos.cod_tarifario')}><input className={fieldCls} value={form.codigo_tarifario} onChange={(e) => set({ codigo_tarifario: e.target.value })} /></Campo>
                <Campo label={t('productos.cod_cum')}><input className={fieldCls} value={form.codigo_cum} onChange={(e) => set({ codigo_cum: e.target.value })} /></Campo>
              </div>
            </section>

            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label={t('comun.activo')} />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>{t('comun.cancelar')}</Button>
                <Button isLoading={guardar.isPending} disabled={!form.nombre.trim() || (!!editId && !form.codigo.trim())} onClick={() => guardar.mutate()}>{t('comun.guardar')}</Button>
              </div>
            </div>
            </>)}

            {tab === 'variantes' && (
              editId
                ? <VariantesEditor productoId={editId} categoriaId={form.categoria_id} maestroNombre={form.nombre}
                    onChanged={() => void qc.invalidateQueries({ queryKey: ['inv-productos'] })} />
                : <p className="py-10 text-center text-sm text-muted-foreground">{t('productos.guardar_primero_variantes')}</p>
            )}

            {tab === 'descuentos' && (
              editId
                ? <DescuentosEditor productoId={editId} />
                : <p className="py-10 text-center text-sm text-muted-foreground">{t('productos.guardar_primero_descuentos')}</p>
            )}

            {tab === 'imagen' && (
              editId
                ? <ImagenEditor productoId={editId} />
                : <p className="py-10 text-center text-sm text-muted-foreground">{t('productos.guardar_primero_foto')}</p>
            )}
          </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DescuentosEditor({ productoId }: { productoId: string }) {
  const { t } = useTranslation('inventario');
  const qc = useQueryClient();
  const { data: existentes = [] } = useQuery<ProductoDescuento[]>({
    queryKey: ['prod-desc', productoId], queryFn: () => inv.list<ProductoDescuento>('producto_descuentos', `?select=*&producto_id=eq.${productoId}`),
  });
  const [rows, setRows] = useState<Record<string, ProductoDescuento>>({});
  useEffect(() => {
    const m: Record<string, ProductoDescuento> = {};
    for (const L of LETRAS_DESCUENTO) {
      const e = existentes.find((x) => x.letra === L);
      m[L] = e ? { ...e } : { letra: L, descuento_pct: 0, promo_pct: null, promo_desde: null, promo_hasta: null, promo_hora_inicio: null, promo_hora_fin: null, promo_dias: null, vol1_cant: null, vol1_pct: null, vol2_cant: null, vol2_pct: null, vol3_cant: null, vol3_pct: null };
    }
    setRows(m);
  }, [existentes]);
  const set = (L: string, patch: Partial<ProductoDescuento>) => setRows((r) => ({ ...r, [L]: { ...r[L], ...patch } }));
  const n = (v: string): number | null => (v === '' ? null : Number(v));
  const inp = 'h-8 w-20 rounded border border-input bg-background px-2 text-right text-sm';

  const guardar = useMutation({
    mutationFn: async () => {
      for (const L of LETRAS_DESCUENTO) {
        const r = rows[L]; if (!r) continue;
        if (!r.promo_hora_inicio !== !r.promo_hora_fin) throw new Error(t('productos.err_franja', { letra: L }));
        const body = {
          producto_id: productoId, letra: L, descuento_pct: Number(r.descuento_pct) || 0,
          promo_pct: r.promo_pct, promo_desde: r.promo_desde || null, promo_hasta: r.promo_hasta || null,
          promo_hora_inicio: r.promo_hora_inicio || null, promo_hora_fin: r.promo_hora_fin || null,
          promo_dias: r.promo_dias?.length ? r.promo_dias : null,
          vol1_cant: r.vol1_cant, vol1_pct: r.vol1_pct, vol2_cant: r.vol2_cant, vol2_pct: r.vol2_pct, vol3_cant: r.vol3_cant, vol3_pct: r.vol3_pct,
        };
        if (r.id) await inv.update('producto_descuentos', r.id, body);
        else await inv.create('producto_descuentos', body);
      }
    },
    onSuccess: () => { toast.success(t('productos.desc_guardados')); void qc.invalidateQueries({ queryKey: ['prod-desc', productoId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">{t('productos.desc_ayuda')}</p>
        <div className="overflow-x-auto">
          <table className="text-sm">
            <thead><tr className="border-b text-xs text-muted-foreground">
              <th className="px-1 py-1">{t('productos.d_letra')}</th><th className="px-1">{t('productos.d_base')}</th>
              <th className="px-1">{t('productos.d_promo')}</th><th className="px-1">{t('productos.d_desde')}</th><th className="px-1">{t('productos.d_hasta')}</th>
              <th className="px-1">{t('productos.d_hora_ini')}</th><th className="px-1">{t('productos.d_hora_fin')}</th><th className="px-1">{t('productos.d_dias')}</th>
              <th className="px-1">{t('productos.d_vcant', { n: 1 })}</th><th className="px-1">V1 %</th>
              <th className="px-1">{t('productos.d_vcant', { n: 2 })}</th><th className="px-1">V2 %</th>
              <th className="px-1">{t('productos.d_vcant', { n: 3 })}</th><th className="px-1">V3 %</th>
            </tr></thead>
            <tbody>
              {LETRAS_DESCUENTO.map((L) => { const r = rows[L]; if (!r) return null; return (
                <tr key={L} className="border-b last:border-0">
                  <td className="px-1 py-1 text-center font-semibold">{L}</td>
                  <td className="px-1"><input type="number" step="0.01" className={inp} value={r.descuento_pct} onChange={(e) => set(L, { descuento_pct: Number(e.target.value) || 0 })} /></td>
                  <td className="px-1"><input type="number" step="0.01" className={inp} value={r.promo_pct ?? ''} onChange={(e) => set(L, { promo_pct: n(e.target.value) })} /></td>
                  <td className="px-1"><input type="date" className={`${inp} w-32`} value={r.promo_desde ?? ''} onChange={(e) => set(L, { promo_desde: e.target.value || null })} /></td>
                  <td className="px-1"><input type="date" className={`${inp} w-32`} value={r.promo_hasta ?? ''} onChange={(e) => set(L, { promo_hasta: e.target.value || null })} /></td>
                  <td className="px-1"><input type="time" className={`${inp} w-24`} value={r.promo_hora_inicio?.slice(0, 5) ?? ''} onChange={(e) => set(L, { promo_hora_inicio: e.target.value || null })} /></td>
                  <td className="px-1"><input type="time" className={`${inp} w-24`} value={r.promo_hora_fin?.slice(0, 5) ?? ''} onChange={(e) => set(L, { promo_hora_fin: e.target.value || null })} /></td>
                  <td className="px-1">
                    <div className="flex gap-0.5">
                      {DIAS_SEMANA_MIN.map((d, i) => {
                        const on = (r.promo_dias ?? []).includes(i);
                        return (
                          <button key={i} type="button" title={d}
                            className={`h-6 w-6 rounded border text-[10px] font-semibold ${on ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted text-muted-foreground'}`}
                            onClick={() => set(L, { promo_dias: on ? (r.promo_dias ?? []).filter((x) => x !== i) : [...(r.promo_dias ?? []), i].sort((a, b) => a - b) })}>
                            {d}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-1"><input type="number" step="0.01" className={inp} value={r.vol1_cant ?? ''} onChange={(e) => set(L, { vol1_cant: n(e.target.value) })} /></td>
                  <td className="px-1"><input type="number" step="0.01" className={inp} value={r.vol1_pct ?? ''} onChange={(e) => set(L, { vol1_pct: n(e.target.value) })} /></td>
                  <td className="px-1"><input type="number" step="0.01" className={inp} value={r.vol2_cant ?? ''} onChange={(e) => set(L, { vol2_cant: n(e.target.value) })} /></td>
                  <td className="px-1"><input type="number" step="0.01" className={inp} value={r.vol2_pct ?? ''} onChange={(e) => set(L, { vol2_pct: n(e.target.value) })} /></td>
                  <td className="px-1"><input type="number" step="0.01" className={inp} value={r.vol3_cant ?? ''} onChange={(e) => set(L, { vol3_cant: n(e.target.value) })} /></td>
                  <td className="px-1"><input type="number" step="0.01" className={inp} value={r.vol3_pct ?? ''} onChange={(e) => set(L, { vol3_pct: n(e.target.value) })} /></td>
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>{t('productos.guardar_descuentos')}</Button>
        </div>
      </div>
  );
}

// Editor de imagen del producto. Redimensiona en el navegador y guarda en la
// tabla satélite producto_imagenes (thumb ~160px + imagen ~800px).
function ImagenEditor({ productoId }: { productoId: string }) {
  const { t } = useTranslation('inventario');
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['prod-img', productoId], queryFn: () => getProductoImagen(productoId) });
  const [subiendo, setSubiendo] = useState(false);

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['prod-img', productoId] });
    void qc.invalidateQueries({ queryKey: ['pos-prod-thumbs'] });  // el POS reusa thumbs
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error(t('productos.img_seleccionar')); return; }
    setSubiendo(true);
    try {
      const imagen = await resizeToDataURL(file, 800, 0.75);
      const thumb = await resizeToDataURL(file, 160, 0.6);
      await upsertProductoImagen(productoId, { mime: 'image/jpeg', imagen, thumb });
      toast.success(t('productos.img_guardada'));
      refrescar();
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setSubiendo(false); }
  };

  const quitar = useMutation({
    mutationFn: () => deleteProductoImagen(productoId),
    onSuccess: () => { toast.success(t('productos.img_eliminada')); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const actual = data?.imagen ?? data?.thumb ?? null;

  return (
    <div className="space-y-4 py-2">
      <div className="flex items-start gap-4">
        <div className="flex h-40 w-40 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-muted/30">
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            : actual ? <img src={actual} alt={t('productos.img_alt')} className="h-full w-full object-contain" />
            : <ImageIcon className="h-10 w-10 text-muted-foreground/40" />}
        </div>
        <div className="space-y-2 text-sm">
          <p className="text-muted-foreground">{t('productos.img_ayuda')}</p>
          <div className="flex gap-2">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 hover:bg-muted">
              {subiendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              <span>{actual ? t('productos.img_cambiar') : t('productos.img_subir')}</span>
              <input type="file" accept="image/*" className="hidden" disabled={subiendo}
                onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
            {actual && <Button variant="outline" isLoading={quitar.isPending} onClick={() => quitar.mutate()}>{t('productos.img_quitar')}</Button>}
          </div>
          <p className="text-xs text-muted-foreground">{t('productos.img_formatos')}</p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor de VARIANTES (SKU) de un producto maestro.
// Selecciona atributos + valores → genera el producto-cruz vía RPC
// inventario.variantes_generar. Muestra las variantes en matriz (2 atributos)
// o en lista, con edición de precio, activar/desactivar y eliminar.
// ---------------------------------------------------------------------------
interface AtrRow { id: string; nombre: string; tipo: string; genera_variante: boolean; es_activo: boolean; orden: number }
interface ValRow { id: string; atributo_id: string; valor: string; abreviatura: string | null; es_activo: boolean; orden: number }
interface VarRow { id: string; codigo: string; nombre: string; precio_base: number; es_activo: boolean }
interface VVRow { producto_id: string; atributo_id: string; valor_id: string }

function cartesian<T>(lists: T[][]): T[][] {
  return lists.reduce<T[][]>((acc, list) => acc.flatMap((a) => list.map((x) => [...a, x])), [[]]);
}

function VariantesEditor({ productoId, categoriaId, maestroNombre, onChanged }: {
  productoId: string; categoriaId: string; maestroNombre: string; onChanged: () => void;
}) {
  const { t } = useTranslation('inventario');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [sel, setSel] = useState<Record<string, string[]>>({});

  const { data: atributos = [] } = useQuery<AtrRow[]>({ queryKey: ['inv-atributos'], queryFn: () => inv.list<AtrRow>('atributos', '?order=orden,nombre') });
  const { data: valores = [] } = useQuery<ValRow[]>({ queryKey: ['inv-valores-all'], queryFn: () => inv.list<ValRow>('atributo_valores', '?order=orden,valor') });
  const { data: catAtrs = [] } = useQuery<{ atributo_id: string }[]>({
    queryKey: ['prod-catatr', categoriaId],
    queryFn: () => (categoriaId ? inv.list<{ atributo_id: string }>('categoria_atributos', `?categoria_id=eq.${categoriaId}&select=atributo_id`) : Promise.resolve([])),
    enabled: !!categoriaId,
  });
  const { data: variantes = [] } = useQuery<VarRow[]>({
    queryKey: ['prod-variantes', productoId],
    queryFn: () => inv.list<VarRow>('productos', `?parent_id=eq.${productoId}&select=id,codigo,nombre,precio_base,es_activo&order=codigo`),
  });
  const ids = variantes.map((v) => v.id);
  const { data: vvals = [] } = useQuery<VVRow[]>({
    queryKey: ['prod-vvals', productoId, ids.join(',')],
    queryFn: () => (ids.length ? inv.list<VVRow>('producto_variante_valores', `?producto_id=in.(${ids.join(',')})&select=producto_id,atributo_id,valor_id`) : Promise.resolve([])),
    enabled: ids.length > 0,
  });

  const refetchAll = () => { void qc.invalidateQueries({ queryKey: ['prod-variantes', productoId] }); void qc.invalidateQueries({ queryKey: ['prod-vvals', productoId] }); };

  const atrsDisp = atributos.filter((a) => a.genera_variante && a.es_activo && (catAtrs.length === 0 || catAtrs.some((c) => c.atributo_id === a.id)));
  const valDe = (aid: string) => valores.filter((v) => v.atributo_id === aid && v.es_activo);
  const toggleVal = (aid: string, vid: string) => setSel((s) => {
    const cur = s[aid] || []; return { ...s, [aid]: cur.includes(vid) ? cur.filter((x) => x !== vid) : [...cur, vid] };
  });

  const incluidos = atrsDisp.filter((a) => (sel[a.id] || []).length > 0);
  const combos = incluidos.length
    ? cartesian(incluidos.map((a) => (sel[a.id]).map((vid) => ({ atributo_id: a.id, valor_id: vid }))))
    : [];

  const generar = useMutation({
    mutationFn: () => invRpc<{ ok: boolean; creadas: number; omitidas: number }>('variantes_generar', { _maestro_id: productoId, _combos: combos }),
    onSuccess: (r) => { toast.success(`${t('productos.v_creadas', { n: r.creadas })}${r.omitidas ? ` · ${t('productos.v_omitidas', { n: r.omitidas })}` : ''}`); setSel({}); refetchAll(); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const actualizar = useMutation({
    mutationFn: (p: { id: string; body: Record<string, unknown> }) => inv.update('productos', p.id, p.body),
    onSuccess: () => { refetchAll(); onChanged(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => inv.remove('productos', id),
    onSuccess: () => { toast.success(t('productos.v_eliminada')); refetchAll(); onChanged(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });

  const valTxt = new Map(valores.map((v) => [v.id, v.valor]));
  const comboDe = new Map<string, Record<string, string>>();
  for (const vv of vvals) { const m = comboDe.get(vv.producto_id) || {}; m[vv.atributo_id] = vv.valor_id; comboDe.set(vv.producto_id, m); }
  const usedAttrs = atributos.filter((a) => vvals.some((vv) => vv.atributo_id === a.id));

  const precioInput = (v: VarRow) => (
    <input type="number" step="0.0001" defaultValue={v.precio_base} className="h-8 w-24 rounded border border-input bg-background px-2 text-right text-xs"
      onBlur={(e) => { const n = Number(e.target.value); if (n !== Number(v.precio_base)) actualizar.mutate({ id: v.id, body: { precio_base: n } }); }} />
  );

  // Índice para matriz 2D
  const cellIndex = new Map<string, VarRow>();
  if (usedAttrs.length === 2) {
    const [A, B] = usedAttrs;
    for (const v of variantes) { const m = comboDe.get(v.id); if (m?.[A.id] && m?.[B.id]) cellIndex.set(`${m[A.id]}|${m[B.id]}`, v); }
  }

  return (
    <div className="space-y-4">
      {dlg.ui}
      {/* Generador */}
      <section className="space-y-3 rounded-md border p-3">
        <p className="text-sm font-semibold">{t('productos.v_generar_titulo')}</p>
        {atrsDisp.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('productos.v_sin_atributos_a')} <b>{t('productos.v_ruta_atributos')}</b> {t('productos.v_sin_atributos_b')}</p>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {atrsDisp.map((a) => (
                <div key={a.id} className="rounded-md border p-2">
                  <p className="text-xs font-semibold mb-1">{a.nombre}</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {valDe(a.id).length === 0 && <span className="text-xs text-muted-foreground">{t('productos.v_sin_valores')}</span>}
                    {valDe(a.id).map((v) => (
                      <label key={v.id} className="flex items-center gap-1 text-sm cursor-pointer">
                        <input type="checkbox" className="h-3.5 w-3.5" checked={(sel[a.id] || []).includes(v.id)} onChange={() => toggleVal(a.id, v.id)} /> {v.valor}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{combos.length > 0 ? t('productos.v_se_generaran', { n: combos.length }) : t('productos.v_selecciona')}</span>
              <Button size="sm" isLoading={generar.isPending} disabled={combos.length === 0} onClick={() => generar.mutate()}>
                <Plus className="w-4 h-4 mr-1" /> {t('productos.v_generar')} {combos.length > 0 ? `(${combos.length})` : ''}
              </Button>
            </div>
          </>
        )}
      </section>

      {/* Variantes existentes */}
      <section className="space-y-2">
        <p className="text-sm font-semibold">{t('productos.v_de', { nombre: maestroNombre, n: variantes.length })}</p>
        {variantes.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t('productos.v_ninguna')}</p>
        ) : usedAttrs.length === 2 ? (
          <div className="overflow-auto">
            <table className="text-sm border-collapse">
              <thead>
                <tr>
                  <th className="border p-2 bg-muted text-xs">{usedAttrs[0].nombre} ＼ {usedAttrs[1].nombre}</th>
                  {valDe(usedAttrs[1].id).filter((c) => variantes.some((v) => comboDe.get(v.id)?.[usedAttrs[1].id] === c.id)).map((c) => (
                    <th key={c.id} className="border p-2 bg-muted text-xs whitespace-nowrap">{c.valor}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {valDe(usedAttrs[0].id).filter((r) => variantes.some((v) => comboDe.get(v.id)?.[usedAttrs[0].id] === r.id)).map((r) => (
                  <tr key={r.id}>
                    <th className="border p-2 bg-muted/50 text-xs text-left whitespace-nowrap">{r.valor}</th>
                    {valDe(usedAttrs[1].id).filter((c) => variantes.some((v) => comboDe.get(v.id)?.[usedAttrs[1].id] === c.id)).map((c) => {
                      const v = cellIndex.get(`${r.id}|${c.id}`);
                      return (
                        <td key={c.id} className="border p-2 align-top">
                          {v ? (
                            <div className="space-y-1">
                              <div className="font-mono text-[11px] text-muted-foreground">{v.codigo}</div>
                              {precioInput(v)}
                              <div className="flex items-center gap-2">
                                <Switch checked={v.es_activo} onChange={(x) => actualizar.mutate({ id: v.id, body: { es_activo: x } })} label="" />
                                <button className="text-red-500" title={t('productos.eliminar')} onClick={async () => { if (await dlg.confirmar(t('productos.v_confirmar_eliminar', { codigo: v.codigo }), { peligro: true })) eliminar.mutate(v.id); }}><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            </div>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">{t('productos.codigo')}</th><th className="py-2 pr-3">{t('productos.v_combinacion')}</th><th className="py-2 pr-3 text-right">{t('productos.v_precio')}</th><th className="py-2 pr-3">{t('comun.activo')}</th><th className="py-2"></th>
              </tr></thead>
              <tbody>
                {variantes.map((v) => {
                  const m = comboDe.get(v.id) || {};
                  const combo = usedAttrs.map((a) => valTxt.get(m[a.id]) || '').filter(Boolean).join(' · ');
                  return (
                    <tr key={v.id} className="border-b last:border-0">
                      <td className="py-1.5 pr-3 font-mono text-xs">{v.codigo}</td>
                      <td className="py-1.5 pr-3">{combo || '—'}</td>
                      <td className="py-1.5 pr-3 text-right">{precioInput(v)}</td>
                      <td className="py-1.5 pr-3"><Switch checked={v.es_activo} onChange={(x) => actualizar.mutate({ id: v.id, body: { es_activo: x } })} label="" /></td>
                      <td className="py-1.5 text-right"><button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title={t('productos.eliminar')} onClick={async () => { if (await dlg.confirmar(t('productos.v_confirmar_eliminar', { codigo: v.codigo }), { peligro: true })) eliminar.mutate(v.id); }}><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {variantes.length > 0 && <p className="text-xs text-muted-foreground">{t('productos.v_nota')}</p>}
      </section>
    </div>
  );
}
